import { useEffect, useState } from 'react'
import type { BuildDesign } from '@/types'

const ROOT = '/build-assets/20261020-v1/'
const FONTS = { boy: ['StampBoy', 'tag-boy.otf'], 'dirty-lizard': ['StampDirtyLizard', 'tag-dirty-lizard.otf'], retro: ['StampRetro', 'tag-retro.otf'] } as const
const fontLoads = new Map<string, Promise<FontFace>>()

/** Render only production artwork, never editor borders, handles, or controls. */
async function preview(design: BuildDesign, source: string, side: 'front' | 'back') {
  const image = (url: string) => new Promise<HTMLImageElement>((resolve, reject) => {
    const picture = new Image()
    picture.onload = () => resolve(picture)
    picture.onerror = () => reject(new Error('Preview image unavailable'))
    picture.src = url
  })
  await Promise.all(Object.values(FONTS).map(([name, file]) => {
    if (!fontLoads.has(name)) fontLoads.set(name, new FontFace(name, `url(${ROOT}${file})`).load().then(font => { document.fonts.add(font); return font }))
    return fontLoads.get(name)
  }))
  const newCase = design.object === 'airpods' || design.object === 'cricket'
  const faceSource = newCase ? source.replace(/-front\.webp$/, `-${side}.webp`)
    : design.base === 'Grey' && side === 'back' ? source.replace(/base-grey\.png$/, 'base-grey-back.png') : source
  const base = await image(faceSource)
  const mask = newCase ? await image(faceSource.replace(/\.webp$/, '-mask.png')) : base
  const canvas = document.createElement('canvas')
  canvas.width = 600
  canvas.height = Math.round(600 * base.naturalHeight / base.naturalWidth)
  const context = canvas.getContext('2d')!
  const artwork = document.createElement('canvas')
  artwork.width = canvas.width; artwork.height = canvas.height
  const ink = artwork.getContext('2d')!
  context.fillStyle = '#FFFFFF'
  context.fillRect(0, 0, canvas.width, canvas.height)
  context.drawImage(base, 0, 0, canvas.width, canvas.height)
  const layers = [
    ...design.tags.filter(tag => tag.placed && tag.side === side).map((tag, index) => ({ kind: 'tag' as const, value: tag, order: tag.order ?? index + 1 })),
    ...design.decor.filter(item => item.side === side).map((item, index) => ({ kind: 'decor' as const, value: item, order: item.order ?? design.tags.length + index + 1 }))
  ].sort((a, b) => a.kind === b.kind ? a.order - b.order : a.kind === 'tag' ? -1 : 1)
  for (const layer of layers) {
    if (layer.kind === 'tag') {
      const tag = layer.value
      const available = Math.max(12, tag.width * canvas.width - 25)
      const family = FONTS[tag.font][0]
      ink.font = `400 64px ${family}`
      const size = Math.min(available * .8, 64 * available / Math.max(1, ink.measureText(tag.text).width))
      ink.save()
      ink.translate(tag.x * canvas.width, tag.y * canvas.height)
      ink.rotate(tag.rotation * Math.PI / 180)
      ink.font = `400 ${size}px ${family}`
      ink.fillStyle = tag.color
      if (tag.color === '#C0C0C0' || tag.color === '#D4AF37') {
        const finish = ink.createLinearGradient(0, -size * .55, 0, size * .55)
        const stops = tag.color === '#C0C0C0' ? ['#6f757a', '#fdfffc', '#a6abb0', '#f5f7f8', '#6f757a'] : ['#8b6510', '#fff1aa', '#c39626', '#ffe69a', '#997012']
        ;[0, .28, .49, .63, 1].forEach((offset, index) => finish.addColorStop(offset, stops[index]))
        ink.fillStyle = finish
      }
      ink.textAlign = 'center'
      const metrics = ink.measureText(tag.text)
      const baseline = (metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent) / 2
      if (tag.strokeWidth) {
        ink.strokeStyle = tag.strokeColor || '#000000'
        ink.lineWidth = size * tag.strokeWidth / 100
        ink.strokeText(tag.text, 0, baseline)
      }
      ink.fillText(tag.text, 0, baseline)
      ink.restore()
    } else {
      const decor = layer.value
      const charm = await image(`${ROOT}decor-${decor.name.toLowerCase().replace(/ /g, '-')}.png`)
      const size = decor.width * canvas.width
      const scale = size / Math.max(charm.naturalWidth, charm.naturalHeight)
      const width = charm.naturalWidth * scale
      const height = charm.naturalHeight * scale
      ink.save()
      ink.translate(decor.x * canvas.width, decor.y * canvas.height)
      ink.rotate((decor.rotation || 0) * Math.PI / 180)
      ink.drawImage(charm, -width / 2, -height / 2, width, height)
      ink.restore()
    }
  }
  ink.globalCompositeOperation = 'destination-in'
  ink.drawImage(mask, 0, 0, artwork.width, artwork.height)
  context.drawImage(artwork, 0, 0)
  return canvas.toDataURL('image/png')
}

/** The saved layout is the source of truth for both checkout previews and the seller link. */
export function BuildPreview({ design, source }: { design: BuildDesign; source: string }) {
  const [pictures, setPictures] = useState<string[]>([])
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let active = true
    setFailed(false)
    Promise.all([preview(design, source, 'front'), preview(design, source, 'back')])
      .then(images => { if (active) setPictures(images) })
      .catch(() => { if (active) setFailed(true) })
    return () => { active = false }
  }, [design, source])
  if (failed) return <p role="status" className="text-sm text-ink-muted">Preview unavailable. Your saved design is still attached below.</p>
  return <div data-testid="build-clean-preview" className="mb-8 flex justify-center gap-8 rounded-lg border border-hairline bg-white p-5">
    {['Front', 'Back'].map((side, index) => <figure key={side} className="min-w-0 text-center">
      {pictures[index] ? <img src={pictures[index]} alt={`${side} — your custom skin`} className="mx-auto h-72 max-w-full object-contain md:h-96" /> : <div role="status" className="h-72 text-sm text-gray-600 md:h-96">Preparing preview…</div>}
      <figcaption className="mt-3 text-sm text-gray-700">{side}</figcaption>
    </figure>)}
  </div>
}
